import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCasesStore } from '../store/casesStore';
import { casesApi } from '../api/cases';
import type { Case, TokenUsageEntry, CaseHistoryEntry } from '../types';

interface UseCaseReviewParams {
  caseNumber: string | undefined;
}

export function useCaseReview({ caseNumber }: UseCaseReviewParams) {
  const navigate = useNavigate();

  // Store actions
  const translateCases = useCasesStore((state) => state.translateCases);
  const approveCase = useCasesStore((state) => state.approveCase);
  const unapproveCase = useCasesStore((state) => state.unapproveCase);
  const restartCase = useCasesStore((state) => state.restartCase);

  // Case data state
  const [currentCase, setCurrentCase] = useState<Case | null>(null);
  const [prevCaseNumber, setPrevCaseNumber] = useState<string | null>(null);
  const [nextCaseNumber, setNextCaseNumber] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [caseNotFound, setCaseNotFound] = useState(false);

  // Original PDF state
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [pdfLoading, setPdfLoading] = useState(true);

  // Translated PDF state
  const [translatedPdfUrl, setTranslatedPdfUrl] = useState<string | null>(null);
  const [translatedPdfLoading, setTranslatedPdfLoading] = useState(false);

  // HTML editor state
  const [htmlContent, setHtmlContent] = useState<string | null>(null);
  const [htmlLoading, setHtmlLoading] = useState(false);
  const [htmlError, setHtmlError] = useState<string | null>(null);

  // View mode: 'pdf' or 'html'
  const [viewMode, setViewMode] = useState<'pdf' | 'html'>('pdf');

  // Regenerating PDF state
  const [isRegenerating, setIsRegenerating] = useState(false);

  // Case history state
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyData, setHistoryData] = useState<CaseHistoryEntry[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [diffModalOpen, setDiffModalOpen] = useState(false);
  const [selectedDiff, setSelectedDiff] = useState<string | null>(null);

  // Token usage history state
  const [tokenUsageData, setTokenUsageData] = useState<TokenUsageEntry[]>([]);
  const [tokenUsageLoading, setTokenUsageLoading] = useState(false);

  // Salesforce status state
  const [sfStatusOpen, setSfStatusOpen] = useState(false);
  const [sfStatusData, setSfStatusData] = useState<{
    salesforce_status: string | null;
    last_modified: string | null;
  } | null>(null);
  const [sfStatusLoading, setSfStatusLoading] = useState(false);

  // Restart confirmation state
  const [restartConfirmOpen, setRestartConfirmOpen] = useState(false);

  // Fetch case data from efficient single-case endpoint
  const fetchCaseDetail = useCallback(async (cn: string, skipLoading = false) => {
    if (!skipLoading) {
      setIsLoading(true);
    }
    setCaseNotFound(false);
    try {
      const response = await casesApi.getDetail(cn);
      setCurrentCase(response.case);
      setPrevCaseNumber(response.prev_case_number);
      setNextCaseNumber(response.next_case_number);
    } catch (err) {
      console.error('Failed to fetch case detail:', err);
      setCaseNotFound(true);
      setCurrentCase(null);
    } finally {
      if (!skipLoading) {
        setIsLoading(false);
      }
    }
  }, []);

  // Refetch case data
  const refetchCase = useCallback(() => {
    if (caseNumber) {
      fetchCaseDetail(caseNumber);
    }
  }, [caseNumber, fetchCaseDetail]);

  // Load translated PDF URL
  const loadTranslatedPdfUrl = useCallback(async (cn: string) => {
    setTranslatedPdfLoading(true);
    try {
      const response = await casesApi.getTranslatedPdfUrl(cn);
      setTranslatedPdfUrl(response.url);
    } catch (err) {
      console.error('Failed to get translated PDF URL:', err);
      setTranslatedPdfUrl(null);
    } finally {
      setTranslatedPdfLoading(false);
    }
  }, []);

  // Load HTML content
  const loadHtmlContent = useCallback(async (cn: string) => {
    setHtmlLoading(true);
    setHtmlError(null);
    try {
      const response = await casesApi.getTranslationHtml(cn);
      setHtmlContent(response.html);
    } catch (err: unknown) {
      const error = err as { response?: { status: number } };
      if (error.response?.status === 404) {
        setHtmlContent(null);
      } else {
        setHtmlError('Failed to load translation');
        console.error('Failed to get HTML:', err);
      }
    } finally {
      setHtmlLoading(false);
    }
  }, []);

  // Get current status for polling decision
  const currentStatus = currentCase?.status;
  const needsPolling = currentStatus === 'pending' ||
                       currentStatus === 'translating' ||
                       currentStatus === 'generating_pdf';

  // Set page title
  useEffect(() => {
    document.title = caseNumber ? `Review | ${caseNumber}` : 'Review';
  }, [caseNumber]);

  // Poll for case status updates when in transitional states
  useEffect(() => {
    if (!needsPolling || !caseNumber) return;
    const pollInterval = setInterval(() => {
      fetchCaseDetail(caseNumber, true);
    }, 2000);
    return () => clearInterval(pollInterval);
  }, [needsPolling, caseNumber, fetchCaseDetail]);

  // Track previous status to detect when translation completes
  const prevStatusRef = useRef(currentStatus);
  useEffect(() => {
    const prevStatus = prevStatusRef.current;
    prevStatusRef.current = currentStatus;

    if ((prevStatus === 'translating' || prevStatus === 'generating_pdf') &&
        (currentStatus === 'ready_for_review' || currentStatus === 'translated')) {
      if (caseNumber) {
        loadHtmlContent(caseNumber);
        loadTranslatedPdfUrl(caseNumber);
      }
    }
  }, [currentStatus, caseNumber, loadHtmlContent, loadTranslatedPdfUrl]);

  // Fetch case data on mount and when caseNumber changes
  useEffect(() => {
    if (caseNumber) {
      fetchCaseDetail(caseNumber);
    }
  }, [caseNumber, fetchCaseDetail]);

  // Reset PDF state when case number changes
  const prevCaseNumberRef = useRef(caseNumber);
  const pdfFetchedForCaseRef = useRef<string | null>(null);
  useEffect(() => {
    if (prevCaseNumberRef.current !== caseNumber) {
      prevCaseNumberRef.current = caseNumber;
      pdfFetchedForCaseRef.current = null;
      setPdfUrl(null);
      setPdfLoading(true);
      setTranslatedPdfUrl(null);
      setHtmlContent(null);
    }
  }, [caseNumber]);

  // Fetch presigned URL for original PDF
  const canFetchPdf = currentStatus && currentStatus !== 'pending';
  useEffect(() => {
    if (!caseNumber || !canFetchPdf) return;
    if (pdfFetchedForCaseRef.current === caseNumber) return;

    pdfFetchedForCaseRef.current = caseNumber;
    setPdfLoading(true);
    casesApi.getOriginalPdfUrl(caseNumber)
      .then((response) => {
        setPdfUrl(response.url);
        setPdfLoading(false);
      })
      .catch((err) => {
        console.error('Failed to get PDF URL:', err);
        setPdfLoading(false);
      });
  }, [caseNumber, canFetchPdf]);

  // Load content when case changes
  useEffect(() => {
    if (!caseNumber) return;
    loadHtmlContent(caseNumber);
    loadTranslatedPdfUrl(caseNumber);
  }, [caseNumber, loadHtmlContent, loadTranslatedPdfUrl]);

  // Clear panels when navigating
  const clearPanels = useCallback(() => {
    setHistoryOpen(false);
    setHistoryData([]);
    setTokenUsageData([]);
    setSfStatusOpen(false);
    setSfStatusData(null);
  }, []);

  // Action handlers
  const handleTranslate = useCallback(async () => {
    if (!caseNumber || !currentCase) return;
    setCurrentCase({ ...currentCase, status: 'translating' });
    await translateCases([caseNumber]);
  }, [caseNumber, currentCase, translateCases]);

  const handleApprove = useCallback(async () => {
    if (!caseNumber) return;
    await approveCase(caseNumber);
    clearPanels();
    if (nextCaseNumber) {
      navigate(`/review/${nextCaseNumber}`);
    } else {
      navigate('/');
    }
  }, [caseNumber, approveCase, clearPanels, nextCaseNumber, navigate]);

  const handleUnapprove = useCallback(async () => {
    if (!caseNumber) return;
    await unapproveCase(caseNumber);
    refetchCase();
  }, [caseNumber, unapproveCase, refetchCase]);

  const handleStatusChange = useCallback(async (newStatus: string) => {
    if (!caseNumber || !currentCase) return;
    setCurrentCase({ ...currentCase, status: newStatus as Case['status'] });
    try {
      await casesApi.changeStatus(caseNumber, newStatus);
      refetchCase();
    } catch (err) {
      console.error('Failed to change status:', err);
      refetchCase();
    }
  }, [caseNumber, currentCase, refetchCase]);

  const handleRestartClick = useCallback(() => {
    setRestartConfirmOpen(true);
  }, []);

  const handleRestartConfirm = useCallback(() => {
    if (!caseNumber || !currentCase) return;
    setRestartConfirmOpen(false);
    setPdfUrl(null);
    setPdfLoading(true);
    setTranslatedPdfUrl(null);
    setHtmlContent(null);
    setCurrentCase({ ...currentCase, status: 'pending' });
    restartCase(caseNumber);
  }, [caseNumber, currentCase, restartCase]);

  const handleRestartCancel = useCallback(() => {
    setRestartConfirmOpen(false);
  }, []);

  const handleToggleHistory = useCallback(async () => {
    if (!caseNumber) return;
    if (historyOpen) {
      setHistoryOpen(false);
      return;
    }
    setHistoryOpen(true);
    setHistoryLoading(true);
    setTokenUsageLoading(true);

    try {
      const [historyResult, tokenUsageResult] = await Promise.allSettled([
        casesApi.getHistory(caseNumber),
        casesApi.getTokenUsage(caseNumber),
      ]);

      if (historyResult.status === 'fulfilled') {
        setHistoryData(historyResult.value);
      } else {
        console.error('Failed to load history:', historyResult.reason);
        setHistoryData([]);
      }

      if (tokenUsageResult.status === 'fulfilled') {
        setTokenUsageData(tokenUsageResult.value);
      } else {
        console.error('Failed to load token usage:', tokenUsageResult.reason);
        setTokenUsageData([]);
      }
    } finally {
      setHistoryLoading(false);
      setTokenUsageLoading(false);
    }
  }, [caseNumber, historyOpen]);

  const handleGetSfStatus = useCallback(async () => {
    if (!caseNumber) return;
    if (sfStatusOpen) {
      setSfStatusOpen(false);
      return;
    }
    setSfStatusOpen(true);
    setSfStatusLoading(true);
    try {
      const data = await casesApi.getSalesforceStatus([caseNumber]);
      const firstResult = data[0];
      if (firstResult) {
        setSfStatusData({
          salesforce_status: firstResult.salesforce_status,
          last_modified: firstResult.last_modified,
        });
      } else {
        setSfStatusData(null);
      }
    } catch (err) {
      console.error('Failed to get SF status:', err);
      setSfStatusData(null);
    } finally {
      setSfStatusLoading(false);
    }
  }, [caseNumber, sfStatusOpen]);

  const handlePopOutPDF = useCallback(() => {
    if (pdfUrl) window.open(pdfUrl, 'pdfPopup', 'width=800,height=900');
  }, [pdfUrl]);

  const handlePopOutTranslation = useCallback(() => {
    if (caseNumber) window.open(`/translation/${caseNumber}`, 'translationPopup', 'width=900,height=900');
  }, [caseNumber]);

  const handleRegeneratePdf = useCallback(async () => {
    if (!caseNumber) return;
    setIsRegenerating(true);
    try {
      await casesApi.regeneratePdf(caseNumber);
      refetchCase();
      const checkPdf = setInterval(async () => {
        try {
          const response = await casesApi.getTranslatedPdfUrl(caseNumber);
          setTranslatedPdfUrl(response.url);
          setIsRegenerating(false);
          clearInterval(checkPdf);
          refetchCase();
        } catch {
          // PDF not ready yet
        }
      }, 2000);
      setTimeout(() => {
        clearInterval(checkPdf);
        setIsRegenerating(false);
      }, 120000);
    } catch (err) {
      console.error('Failed to regenerate PDF:', err);
      setIsRegenerating(false);
    }
  }, [caseNumber, refetchCase]);

  const handleSaveHtml = useCallback(async (html: string) => {
    if (!caseNumber) return;
    await casesApi.updateTranslationHtml(caseNumber, html);
    setHtmlContent(html);
    refetchCase();
    setViewMode('pdf');
    handleRegeneratePdf();
  }, [caseNumber, refetchCase, handleRegeneratePdf]);

  const handleDownloadWord = useCallback(async () => {
    if (!caseNumber) return;
    try {
      const response = await casesApi.getWordUrl(caseNumber);
      window.open(response.url, '_blank');
    } catch (err) {
      console.error('Failed to get Word URL:', err);
    }
  }, [caseNumber]);

  const handleDownloadHtml = useCallback(() => {
    if (!caseNumber || !htmlContent) return;
    const blob = new Blob([htmlContent], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${caseNumber}_translation.html`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [caseNumber, htmlContent]);

  const handleViewDiff = useCallback((diff: string) => {
    setSelectedDiff(diff);
    setDiffModalOpen(true);
  }, []);

  const handleCloseDiffModal = useCallback(() => {
    setDiffModalOpen(false);
  }, []);

  // Computed values
  const isCaseTranslating = currentStatus === 'translating';
  const isRestarting = currentStatus === 'pending';
  const isGeneratingPdf = currentStatus === 'generating_pdf' || isRegenerating;

  return {
    // Case data
    currentCase,
    prevCaseNumber,
    nextCaseNumber,
    isLoading,
    caseNotFound,

    // PDF state
    pdfUrl,
    pdfLoading,
    translatedPdfUrl,
    translatedPdfLoading,

    // HTML state
    htmlContent,
    htmlLoading,
    htmlError,

    // View mode
    viewMode,
    setViewMode,

    // Status flags
    isCaseTranslating,
    isRestarting,
    isRegenerating,
    isGeneratingPdf,

    // History panel
    historyOpen,
    historyData,
    historyLoading,
    tokenUsageData,
    tokenUsageLoading,

    // Diff modal
    diffModalOpen,
    selectedDiff,

    // SF status panel
    sfStatusOpen,
    sfStatusData,
    sfStatusLoading,

    // Restart confirmation
    restartConfirmOpen,

    // Actions
    clearPanels,
    handleTranslate,
    handleApprove,
    handleUnapprove,
    handleStatusChange,
    handleRestartClick,
    handleRestartConfirm,
    handleRestartCancel,
    handleToggleHistory,
    handleGetSfStatus,
    setHistoryOpen,
    setSfStatusOpen,
    handlePopOutPDF,
    handlePopOutTranslation,
    handleSaveHtml,
    handleRegeneratePdf,
    handleDownloadWord,
    handleDownloadHtml,
    handleViewDiff,
    handleCloseDiffModal,
  };
}
